import dayjs from 'dayjs';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, SerializationContext, DataModelBuilder,
  SerializedData, cloneList, isSameList, isSameData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { PoolReservation } from './pool-reservation.js';

export enum PoolCheckInStatus {
    Open = 'OPEN',
    Finished = 'FINISHED',
}

export class PoolCheckIn extends DataModel implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '5aafd6ef93bd498485abf4b27a2d9835';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
        public readonly uniqueId: string,
        public readonly creatorId: string,
        public readonly businessId: string,
        public readonly reservationId: string,
        public readonly assignedKeys: number[],
        public readonly assignedBracelets: number[],
        public readonly hasSlippers: boolean,
        public readonly hasSwimmingCap: boolean,
        public readonly creationDate: dayjs.Dayjs,
        public readonly checkInTime: dayjs.Dayjs,
        public readonly status: PoolCheckInStatus,
        public readonly reservation: PoolReservation,
        public readonly checkOutTime: dayjs.Dayjs | null,
        public readonly assignedLane: number | null,
        public readonly isLaneReserved: boolean,
        public readonly isPoolReserved: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return PoolCheckIn.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PoolCheckIn.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): PoolCheckIn {
    return new PoolCheckIn(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        MapUtils.get<string>(map, 'businessId'),
        MapUtils.get<string>(map, 'reservationId'),
        MapUtils.getList<number>(map, 'assignedKeys', (value) => value as number),
        MapUtils.getList<number>(map, 'assignedBracelets', (value) => value as number),
        MapUtils.get<boolean>(map, 'hasSlippers'),
        MapUtils.get<boolean>(map, 'hasSwimmingCap'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDate(MapUtils.get<number>(map, 'checkInTime')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), PoolCheckInStatus),
        context.fromData<PoolReservation>(MapUtils.get<SerializedData>(map, 'reservation')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'checkOutTime')),
        MapUtils.getOrNull<number>(map, 'assignedLane'),
        MapUtils.get<boolean>(map, 'isLaneReserved'),
        MapUtils.get<boolean>(map, 'isPoolReserved'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'businessId': this.businessId,
      'reservationId': this.reservationId,
      'assignedKeys': this.assignedKeys,
      'assignedBracelets': this.assignedBracelets,
      'hasSlippers': this.hasSlippers,
      'hasSwimmingCap': this.hasSwimmingCap,
      'creationDate': context.encodeDate(this.creationDate),
      'checkInTime': context.encodeDate(this.checkInTime),
      '$status': fromEnumAsString(this.status),
      'reservation': context.toData(this.reservation),
      'checkOutTime': context.encodeDateOrNull(this.checkOutTime),
      'assignedLane': this.assignedLane,
      'isLaneReserved': this.isLaneReserved,
      'isPoolReserved': this.isPoolReserved,
    };
  }

  clone(): this {
    return new PoolCheckIn(
        this.uniqueId,
        this.creatorId,
        this.businessId,
        this.reservationId,
        cloneList(this.assignedKeys),
        cloneList(this.assignedBracelets),
        this.hasSlippers,
        this.hasSwimmingCap,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.checkInTime),
        this.status,
        this.reservation.clone(),
        TimeZone.cloneDateOrNull(this.checkOutTime),
        this.assignedLane,
        this.isLaneReserved,
        this.isPoolReserved,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.businessId === this.businessId &&
        other.reservationId === this.reservationId &&
        isSameList(other.assignedKeys, this.assignedKeys) &&
        isSameList(other.assignedBracelets, this.assignedBracelets) &&
        other.hasSlippers === this.hasSlippers &&
        other.hasSwimmingCap === this.hasSwimmingCap &&
        other.creationDate === this.creationDate &&
        other.checkInTime === this.checkInTime &&
        other.status === this.status &&
        isSameData(other.reservation, this.reservation) &&
        other.checkOutTime === this.checkOutTime &&
        other.assignedLane === this.assignedLane &&
        other.isLaneReserved === this.isLaneReserved &&
        other.isPoolReserved === this.isPoolReserved;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new PoolCheckIn(
        this.uniqueId,
        this.creatorId,
        this.businessId,
        this.reservationId,
        cloneList(this.assignedKeys),
        cloneList(this.assignedBracelets),
        this.hasSlippers,
        this.hasSwimmingCap,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.checkInTime),
        this.status,
        this.reservation.copyWithBusinessRef(businessRef),
        TimeZone.cloneDateOrNull(this.checkOutTime),
        this.assignedLane,
        this.isLaneReserved,
        this.isPoolReserved,
    ) as this;
  }
}

export class PoolCheckInBuilder extends DataModelBuilder<PoolCheckIn> {
  build(context: SerializationContext, map: SerializedData): PoolCheckIn {
    return PoolCheckIn.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PoolCheckIn.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PoolCheckIn.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isLaneReserved', false);
      MapUtils.setIfNullOrUndefined(map, 'isPoolReserved', false);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
